import { useEffect, useRef, type ReactNode } from "react";
import RadioGroup, { type RadioGroupProps } from "@mui/material/RadioGroup";
import FormControlLabel, { type FormControlLabelProps } from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import type { BooleanSwitch, WithSlots } from "types";
import { BooleanSwitchValue } from "consts/general";
import { compareIds } from "util/support";

type Props = WithSlots<
    {
        readonly textInputRequiredWhen?: BooleanSwitch;
    },
    {
        readonly typography: TypographyProps;
        readonly radioGroup: RadioGroupProps;
        readonly textField: TextFieldProps;
        readonly formControlLabel: Partial<FormControlLabelProps>;
    },
    {
        readonly textField: ReactNode;
    }
>;

const InputableRadioGroup = ({
    slotProps,
    slots,
    textInputRequiredWhen = BooleanSwitchValue.On
}: Props) => {
    const textFieldRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

    const value = slotProps?.radioGroup?.value || '';

    useEffect(() => {
        if (compareIds(textInputRequiredWhen, Number(value))) {
            textFieldRef.current?.focus();
        }
    }, [value, textInputRequiredWhen]);

    return (
        <>
            <Typography
                {...slotProps?.typography}
                variant='h6'
            />
            <RadioGroup
                {...slotProps?.radioGroup}
            >
                <FormControlLabel
                    {...slotProps?.formControlLabel}
                    value={BooleanSwitchValue.On}
                    control={<Radio />}
                    label='Yes'
                />
                <FormControlLabel
                    {...slotProps?.formControlLabel}
                    value={BooleanSwitchValue.Off}
                    control={<Radio />}
                    label='No'
                />
            </RadioGroup>
            {slots?.textField ?? (
                <TextField
                    {...slotProps?.textField}
                    inputRef={textFieldRef}
                    required={compareIds(value, textInputRequiredWhen)}
                    multiline
                    fullWidth
                    rows={4}
                    size='small'
                />
            )}
        </>
    );
};

export default InputableRadioGroup;
